<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';

	import { ButtonV2, Icon, Text } from '@Shelly/shelly-design-system';

	import {
		CAMERA_CLOUD_STORAGE_GRADIENT,
		CAMERA_CLOUD_STORAGE_STORAGE_SETTINGS_BUTTON_ID,
	} from 'src/helpers/camera_cloud_storage_helpers';
	import type { CameraCloudStorageActivePlan } from 'src/types/CameraCloudStorage';
	import { CameraCloudStorageSubscriptionStatus } from 'src/types/CameraCloudStorage';

	export let variant: 'subscription' | 'premium-included' = 'subscription';
	export let activePlan: CameraCloudStorageActivePlan | null = null;
	export let usedSlots = 0;
	export let totalSlots = 0;

	const dispatch = createEventDispatcher<{ openStorageSettings: void }>();

	$: isCancelled = activePlan?.status === CameraCloudStorageSubscriptionStatus.Cancelled;
	$: statusLabel = isCancelled
		? $format('text_subscription_cancelled', { default: 'Cancelled' })
		: $format('text_subscription_active', { default: 'Active' });
</script>

<div class="ccs-banner mt-s mb-s" style={`background: ${CAMERA_CLOUD_STORAGE_GRADIENT};`}>
	<div class="d-flex align-items-center gap-s">
		<div class="ccs-banner-icon">
			<Icon size="s" icon="fas fa-camera" />
		</div>
		<div class="flex-1 min-width-0">
			{#if variant === 'premium-included'}
				<Text styleType="controls-large" class="text-bold ccs-text-white">
					{$format('alert_camera_cloud_storage_premium_only_heading', {
						default:
							'You have 1 Camera Cloud Storage slot included in your Premium subscription',
					})}
				</Text>
				<Text styleType="body-small" class="ccs-text-white ccs-opacity-80">
					{$format('alert_camera_cloud_storage_premium_only_message', {
						default:
							'Your Premium subscription includes 1 Camera Cloud Storage slot. Subscribe to a Camera Cloud Storage plan to add more.',
					})}
				</Text>
			{:else if activePlan}
				<Text styleType="controls-large" class="text-bold ccs-text-white">
					{$format('text_camera_cloud_subscription_title', {
						default: `Camera Cloud subscription - ${activePlan.tier} Cameras`,
						values: { count: activePlan.tier },
					})}
				</Text>
				<Text styleType="body-small" class="ccs-text-white ccs-opacity-80">
					{statusLabel}.
					{$format('text_camera_cloud_storage_usage_summary', {
						default: `You currently have are using ${usedSlots} from ${totalSlots} Camera Cloud Storage…`,
						values: { used: usedSlots, total: totalSlots },
					})}
				</Text>
			{/if}
		</div>
	</div>
</div>

<div id={CAMERA_CLOUD_STORAGE_STORAGE_SETTINGS_BUTTON_ID}>
	<ButtonV2 size="m" on:click={() => dispatch('openStorageSettings')} class="text-bold">
		{$format('button_camera_cloud_storage_settings', {
			default: 'Storage Settings',
		})}
	</ButtonV2>
</div>

<style>
	.ccs-banner {
		border-radius: 12px;
		padding: 16px;
		display: flex;
		flex-direction: column;
		gap: 12px;
		color: #eaeaea;
	}

	.ccs-banner-icon {
		width: 32px;
		height: 32px;
		border-radius: 8px;
		background: rgba(255, 255, 255, 0.15);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		color: #eaeaea;
	}

	.flex-1 {
		flex: 1;
	}

	.min-width-0 {
		min-width: 0;
	}

	:global(.ccs-opacity-80),
	:global(.ccs-opacity-80 *) {
		opacity: 0.85;
	}

	:global(.ccs-text-white),
	:global(.ccs-text-white *) {
		color: #ffffff !important;
	}
</style>
